import { IHealthCondition, IMedicalInformation } from '@zeta-types';
import { InferType } from 'yup';
import { healthDefault, healthValidationsSchema } from './form-details';

const mapYesOrNo = (value: boolean) => (value ? 'YES' : 'NO');

export const mapHealthCondition = (healthCondition: IHealthCondition | null | undefined) => {
  if (!healthCondition) return healthDefault;

  return {
    belongType: healthCondition.belongType || '',
    areYouInGoodHealth: mapYesOrNo(healthCondition.inGoodHealth),
    anyMedicalConditions: {
      yesOrNo: mapYesOrNo(healthCondition.anyMedicalConditions?.medicalConditionValue),
      ifYesSpecify: healthCondition.anyMedicalConditions?.medicalConditionDescription || '',
    },
    areYouSmoker: mapYesOrNo(healthCondition.isSmoker),
    anyPreexistingConditionsOrPastSurgeries: {
      yesOrNo: mapYesOrNo(healthCondition.anyPreExistingHealthConditionsOrPastSurgeries?.preExistingHealthConditionsOrPastSurgeries),
      ifYesSpecify: healthCondition.anyPreExistingHealthConditionsOrPastSurgeries?.preExistingHealthConditionsOrPastSurgeriesDescription || '',
    },
    anyHazardousHobbies: {
      yesOrNo: mapYesOrNo(healthCondition.anyHazardousHobbies?.hazardousHobbies),
      ifYesSpecify: healthCondition.anyHazardousHobbies?.hazardousHobbiesDescription || '',
    },
    doYouHavePractitioner: {
      yesOrNo: mapYesOrNo(healthCondition.generalPractioner),
      name: healthCondition.generalPractionerName || '',
      address: healthCondition.generalPractionerAddress || '',
    },
    doYouHaveMedicalInsurance: {
      yesOrNo: mapYesOrNo(healthCondition.medicalInsurance),
      policyNumber: healthCondition.medicalInsurancePolicyNumber || '',
    },
    doYouHaveCriticalIllnessCoverage: {
      yesOrNo: mapYesOrNo(healthCondition.criticalIllnessesCovered),
      policyNumber: healthCondition.criticalIllnessesPolicyNumber || '',
    },
    doYouHaveDentalProceduresCoverage: {
      yesOrNo: mapYesOrNo(healthCondition.dentalProceduresCovered),
      policyNumber: healthCondition.dentalProceduresPolicyNumber || '',
    },
  };
};

export const transformHealthData = (data: IMedicalInformation) => {
  return {
    client: mapHealthCondition(data?.clientHealthCondition),
    partner: mapHealthCondition(data?.partnerHealthCondition),
  };
};

type healthValidationsSchemaType = InferType<typeof healthValidationsSchema>;

export const transformToHealthDetailsRequestPayload = (data: healthValidationsSchemaType) => {
  const mapYesNoToBoolean = (value: string) => value === 'YES';

  const transformHealthCondition = (healthCondition: any) => {
    return {
      belongType: healthCondition.belongType || '',
      inGoodHealth: mapYesNoToBoolean(healthCondition.areYouInGoodHealth),
      anyPreExistingHealthConditionsOrPastSurgeries: {
        preExistingHealthConditionsOrPastSurgeries: mapYesNoToBoolean(healthCondition.anyPreexistingConditionsOrPastSurgeries?.yesOrNo),
        preExistingHealthConditionsOrPastSurgeriesDescription: healthCondition.anyPreexistingConditionsOrPastSurgeries?.ifYesSpecify || '',
      },
      anyMedicalConditions: {
        medicalConditionValue: mapYesNoToBoolean(healthCondition.anyMedicalConditions?.yesOrNo),
        medicalConditionDescription: healthCondition.anyMedicalConditions?.ifYesSpecify || '',
      },
      isSmoker: mapYesNoToBoolean(healthCondition.areYouSmoker),
      anyHazardousHobbies: {
        hazardousHobbies: mapYesNoToBoolean(healthCondition.anyHazardousHobbies?.yesOrNo),
        hazardousHobbiesDescription: healthCondition.anyHazardousHobbies?.ifYesSpecify || '',
      },
      generalPractioner: mapYesNoToBoolean(healthCondition.doYouHavePractitioner?.yesOrNo),
      generalPractionerName: healthCondition.doYouHavePractitioner?.name || '',
      generalPractionerAddress: healthCondition.doYouHavePractitioner?.address || '',
      medicalInsurance: mapYesNoToBoolean(healthCondition.doYouHaveMedicalInsurance?.yesOrNo),
      medicalInsurancePolicyNumber: healthCondition.doYouHaveMedicalInsurance?.policyNumber || '',
      criticalIllnessesCovered: mapYesNoToBoolean(healthCondition.doYouHaveCriticalIllnessCoverage?.yesOrNo),
      criticalIllnessesPolicyNumber: healthCondition.doYouHaveCriticalIllnessCoverage?.policyNumber || '',
      dentalProceduresCovered: mapYesNoToBoolean(healthCondition.doYouHaveDentalProceduresCoverage?.yesOrNo),
      dentalProceduresPolicyNumber: healthCondition.doYouHaveDentalProceduresCoverage?.policyNumber || '',
    };
  };

  return {
    clientHealthCondition: transformHealthCondition(data.client),
    partnerHealthCondition: transformHealthCondition(data.partner),
  };
};
